import { useState, useEffect } from 'react';
import { supabase } from '../config/supabase';
import { useAuth } from '../context/AuthContext';

export function useCollection(tableName, options = {}) {
  const { bypassAgencyFilter = false } = options;
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { agencyId: authAgencyId } = useAuth();
  const agencyId = authAgencyId || (typeof window !== 'undefined' ? window.__AGENCY_ID__ : null);

  const _sbRowToDoc = (row) => {
    if (!row) return null;
    const fields = { ...(row.data || {}), _id: row.id };
    if (row.agency_id) fields.agency_id = row.agency_id;
    if (row.uid) fields.uid = row.uid;
    if (row.status) fields.status = row.status;
    if (row.enabled_modules) fields.enabled_modules = row.enabled_modules;
    if (row.enabled_apis) fields.enabled_apis = row.enabled_apis;
    return { id: row.id, ...fields };
  };

  const fetchData = async () => {
    try {
      let query = supabase.from(tableName).select('*');
      const activeId = agencyId || (typeof window !== 'undefined' ? window.__AGENCY_ID__ : null);
      
      // Filter by agency if not bypassed
      if (!bypassAgencyFilter && activeId) {
        query = query.eq('agency_id', activeId);
      }
      
      const { data: rows, error: err } = await query;
      if (err) throw err;
      
      setData((rows || []).map(_sbRowToDoc));
      setError(null);
    } catch (err) {
      console.error(`Error fetching collection ${tableName}:`, err.message);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const activeId = agencyId || (typeof window !== 'undefined' ? window.__AGENCY_ID__ : null);

    // If not bypassing filter and we don't have an agencyId yet, wait
    if (!bypassAgencyFilter && !activeId) {
      setData([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    fetchData();

    // Setup realtime subscription. Guarded: if WebSocket is unavailable (CSP,
    // network, insecure context…), we keep the fetched data instead of crashing.
    let channel = null;
    try {
      const filter = (!bypassAgencyFilter && activeId) ? `agency_id=eq.${activeId}` : undefined;
      channel = supabase.channel(`rt_${tableName}_${activeId || 'sa'}_${Date.now()}`)
        .on('postgres_changes', {
          event: '*',
          schema: 'public',
          table: tableName,
          ...(filter ? { filter } : {})
        }, () => {
          fetchData();
        })
        .subscribe();
    } catch (rtErr) {
      console.warn(`[useCollection] Realtime disabled for ${tableName}:`, rtErr?.message);
    }

    return () => {
      if (channel) {
        try { supabase.removeChannel(channel); } catch (_) {}
      }
    };
  }, [tableName, agencyId, bypassAgencyFilter]);

  // Insert/Add
  const add = async (payload) => {
    const activeAgencyId = bypassAgencyFilter ? (payload.agency_id || null) : agencyId;
    const row = {
      created_at: new Date().toISOString(),
      ...(activeAgencyId ? { agency_id: activeAgencyId } : {}),
      // If table has special column headers like user_registrations, extract them
      ...(tableName === 'user_registrations' ? {
        uid: payload.uid,
        agency_id: payload.agencyId || payload.uid,
        email: payload.email,
        status: payload.status || 'pending',
        enabled_modules: payload.enabled_modules || [],
        enabled_apis: payload.enabled_apis || []
      } : {}),
      data: payload
    };

    const { data: inserted, error: err } = await supabase
      .from(tableName)
      .insert(row)
      .select('id')
      .single();

    if (err) throw err;

    const newDoc = _sbRowToDoc({ id: inserted.id, ...row });
    setData(prev => [...prev, newDoc]);

    return inserted;
  };

  // Update
  const update = async (id, updates) => {
    // Handle user_registrations special columns
    if (tableName === 'user_registrations') {
      const { status, enabled_modules, enabled_apis, ...rest } = updates;
      const dbUpdates = {};
      if (status !== undefined) dbUpdates.status = status;
      if (enabled_modules !== undefined) dbUpdates.enabled_modules = enabled_modules;
      if (enabled_apis !== undefined) dbUpdates.enabled_apis = enabled_apis;

      const { data: existing } = await supabase
        .from(tableName)
        .select('data')
        .eq('id', id)
        .single();
      
      dbUpdates.data = { ...(existing?.data || {}), ...updates };

      const { error: err } = await supabase
        .from(tableName)
        .update(dbUpdates)
        .eq('id', id);

      if (err) throw err;

      setData(prev => prev.map(item => {
        if (item.id === id) {
          return {
            ...item,
            ...dbUpdates.data,
            id,
            ...(dbUpdates.status !== undefined ? { status: dbUpdates.status } : {}),
            ...(dbUpdates.enabled_modules !== undefined ? { enabled_modules: dbUpdates.enabled_modules } : {}),
            ...(dbUpdates.enabled_apis !== undefined ? { enabled_apis: dbUpdates.enabled_apis } : {})
          };
        }
        return item;
      }));
      return;
    }

    // Default JSONB table updates
    const { data: existing } = await supabase
      .from(tableName)
      .select('data')
      .eq('id', id)
      .single();

    const merged = { ...(existing?.data || {}), ...updates };
    const { error: err } = await supabase
      .from(tableName)
      .update({ data: merged })
      .eq('id', id);

    if (err) throw err;

    setData(prev => prev.map(item => {
      if (item.id === id) {
        return {
          ...item,
          ...merged,
          id
        };
      }
      return item;
    }));
  };

  // Delete
  const remove = async (id) => {
    let query = supabase.from(tableName).delete();
    if (Array.isArray(id)) {
      query = query.in('id', id);
    } else {
      query = query.eq('id', id);
    }
    const { error: err } = await query;

    if (err) throw err;

    setData(prev => {
      const idsToRemove = Array.isArray(id) ? id : [id];
      return prev.filter(item => !idsToRemove.includes(item.id));
    });
  };

  return { data, loading, error, refresh: fetchData, add, update, remove };
}
